/**
 * Get Role By UUID Use Case
 *
 * Fetches a single role by its UUID.
 * Provides extension points for:
 * - Permission-based access control
 * - Logging access attempts
 * - Enrichment with related data
 *
 * @module domains/personnel/application/use-cases/getRoleByUuid
 */

import type { IRoleApiPort } from '@personnel/application/ports/IRoleApiPort';
import type { Role } from '@personnel/domain/entities/Role';

/**
 * Creates a use case to fetch a single role by UUID
 *
 * @param props - Dependencies
 * @param props.apiPort - Role API port implementation
 * @returns Use case with execute method
 *
 * @example
 * const apiPort = createRoleHttpClient({ httpClient: connectApi });
 * const useCase = getRoleByUuidUseCase({ apiPort });
 * const role = await useCase.execute('role-uuid-123');
 */
export const getRoleByUuidUseCase = (props: { apiPort: IRoleApiPort }) => {
  const { apiPort } = props;

  /**
   * Executes role fetching by UUID
   *
   * @param uuid - Role UUID to fetch
   * @returns Promise resolving to Role entity
   * @throws Error if role not found
   *
   * @example
   * const role = await execute('abc-123');
   * console.log(role.name); // "Manager"
   */
  const execute = async (uuid: string): Promise<Role> => {
    // Future business logic could go here:
    // - Validate user has permission to view this role
    // - Log access for auditing
    // - Enrich with user count
    // - Cache frequently accessed roles

    const role = await apiPort.getRoleByUuid(uuid);

    return role;
  };

  return {
    execute,
  };
};
